<template>
  <article
    :class="className"
    @click="folded = $slots.behind ? !folded : false"
    v-view.reveal="parallax ? { handler: viewParallax, delay: 1 } : false"
  >
    <div class="c-card__wrap">
      <div class="c-card__inner">
        <slot name="inner" />
      </div>
      <div v-if="$slots.behind" class="c-card__behind">
        <slot name="behind" />
      </div>
      <span class="c-card__corner"></span>
    </div>
  </article>
</template>

<script>
import Variables from "src/mixins/variables";

import gsap from "gsap";

export default {
  name: "Card",
  props: {
    modifier: {
      type: String,
      default: null
    },
    bg: {
      type: String,
      default: "dark"
    },
    parallax: {
      type: Boolean,
      default: true
    },
    slideOnMounted: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  mixins: [Variables],
  data: () => ({
    x: 0,
    y: 0,
    folded: false
  }),
  mounted() {
    this.slideOnMounted ? this.animationOnMount() : null;
  },
  computed: {
    className() {
      let classname = "c-card";

      if (this.bg) {
        classname += ` -${this.bg}`;
      }

      if (this.folded) {
        classname += " is-folded";
      }

      if (this.modifier) {
        classname += ` -${this.modifier}`;
      }

      return classname;
    }
  },
  methods: {
    animationOnMount() {
      this.x = this.ifHorizontal((0.25 * this.W.w) / 10, 0);
      this.y = this.ifHorizontal(0, (0.5 * this.W.h) / 10);
    },
    viewParallax(e) {
      if (this.isHorizontal) {
        if (e.percent.center.x > 0.75) {
          this.x = ((e.percent.center.x - 0.75) * this.W.w) / 10;
        } else {
          this.x = 0;
        }
      } else {
        if (e.percent.center.y < 0.5) {
          this.y = ((e.percent.center.y - 0.5) * this.W.h) / 15;
        } else {
          this.y = 0;
        }
      }
    }
  },
  watch: {
    x(x) {
      gsap.to(this.$el, {
        duration: 0.2,
        x: x,
        ease: "sine.out"
      });
    },
    y(y) {
      gsap.to(this.$el, {
        duration: 0.2,
        y: y,
        ease: "sine.out"
      });
    },
    isHorizontal(horizontal) {
      if (horizontal) {
        this.y = 0;
      } else {
        this.x = 0;
      }
    }
  }
};
</script>

<style lang="scss">
.c-card {
  --bg-inner: #{$color-dark};
  --bg-behind: #{$color-lighter};
  --bg-corner: #{$color-bright};
  --color-text: #{$color-light};
  --color-text-behind: #{$color-dark};
  --padding-content: 1em;

  // Corner sizes
  --corner-size: 5rem;
  --corner-size-x: calc(var(--corner-size) * 1.5);
  --corner-size-x-perc: calc(100% - var(--corner-size-x));
  --corner-size-y: var(--corner-size);
  --corner-size-y-perc: calc(100% - var(--corner-size-y));

  --clip-path: polygon(0 0, 100% 0, 100% var(--corner-size-y-perc), var(--corner-size-x-perc) 100%, 0 100%);
  --clip-corner: polygon(
    var(--corner-size-x-perc) var(--corner-size-y-perc),
    100% var(--corner-size-y-perc),
    var(--corner-size-x-perc) 100%
  );

  width: 100vw;
  max-width: $card-max-width;
  color: var(--color-text);
  overflow: hidden;

  will-change: transform;

  .c-graph {
    color: var(--color-text);
  }

  &.-fact {
    cursor: pointer;
    box-shadow: $box-shadow;
  }

  &.-place,
  &.-economy {
    --padding-content: 1.5em;

    .c-card__inner {
      justify-content: space-between;
      padding: var(--padding-content);
    }
  }

  &.-economy {
  }

  &.-economy-img {
    max-width: none;
  }

  // Background colors
  &.-bright {
    --bg-inner: #{$color-bright};
    --bg-corner: #{$color-dark};
  }

  &.-light {
    --bg-inner: #{$color-light};
    --color-text: #{$color-bright};
  }

  &.-simple {
    --bg-inner: transparent;
    --color-text: #{$color-dark};

    .c-card__corner {
      display: none;
    }
  }

  &.js-reveal {
    pointer-events: none;

    &.-fact,
    &.-video,
    &.-thumbnail {
      box-shadow: none;
      overflow: visible;

      &:before {
        @include pseudo-el;
        position: absolute;
        top: 0;
        left: 0;
        box-shadow: $box-shadow;
        opacity: 0;
      }
    }

    &.is-visible {
      // transform: translate(0);
      // transition: transform .8s $out-quad;

      &.-fact:before,
      &.-video:before,
      &.-thumbnail {
        opacity: 1;
        transition: opacity 0.2s ease-out 0.8s;
      }
    }

    @supports (clip-path: var(--clip-card)) or (-webkit-clip-path: var(--clip-card)) {
      html:not(.is-edge) & {
        --clip-path: polygon(0 0, 100% 0, 100% 100%, 100% 100%, 0 100%);
        --clip-corner: polygon(
          100% 100%,
          calc(100% + var(--corner-size-x)) var(--corner-size-y-perc),
          var(--corner-size-x-perc) calc(100% + var(--corner-size-y))
        );

        .c-card__wrap {
          clip-path: polygon(-20% -20%, 100% -80%, 100% -60%, -40% 0%);
        }

        &.is-visible {
          --clip-path: polygon(0 0, 100% 0, 100% var(--corner-size-y-perc), var(--corner-size-x-perc) 100%, 0 100%);
          --clip-corner: polygon(
            var(--corner-size-x-perc) var(--corner-size-y-perc),
            100% var(--corner-size-y-perc),
            var(--corner-size-x-perc) 100%
          );

          .c-card__wrap {
            clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
            transition: all 0.8s;
          }

          .c-card__inner,
          .c-card__corner {
            transition: clip-path 0.4s ease-out 0.8s;
          }
        }
      }
    }
  }

  @supports (clip-path: var(--clip-card)) or (-webkit-clip-path: var(--clip-card)) {
    .c-card__wrap {
      transition: clip-path 0.8s $in-out-quad;
    }

    .c-card__inner {
      clip-path: var(--clip-path);
      transition: inherit;
    }

    .c-card__corner {
      width: 100%;
      height: 100%;
      clip-path: var(--clip-corner);
      transition: inherit;
    }

    &.-fact {
      --corner-size: 4rem;
      --corner-size-x: calc(var(--corner-size) * 1.25);

      &:hover {
        --corner-size: 5rem;
      }

      &:hover.is-folded {
        --clip-path: polygon(-60% -60%, 100% 0, 100% -60%, -120% 100%, -100% 100%);
        --clip-corner: polygon(-60% -60%, 100% -60%, -120% 100%);
      }
    }
  }

  @supports not ((clip-path: var(--clip-card)) or (-webkit-clip-path: var(--clip-card))) {
    .c-card__corner {
      clip-path: none;
      width: 0;
      height: 0;
      padding-left: 20%;
      padding-bottom: 20%;
      background-color: var(--bg-behind);
      overflow: hidden;
      transition: transform 0.3s ease-out;
      transform-origin: 100% 100%;

      &:after {
        @include pseudo-el($width: 0, $height: 0);
        margin-left: -500px;
        border-bottom: 500px solid transparent;
        border-left: 500px solid var(--bg-corner);
      }
    }

    &.-fact {
      &:hover {
        .c-card__corner {
          transform: scale(1.3);
        }
      }

      &:hover.is-folded {
        .c-card__inner {
          transform: translate(100%, 2em);
        }

        .c-card__corner {
          transform: scale(0);
        }
      }
    }
  }

  @media #{md("xs")} {
    --padding-content: 1.75em;
    // width: 50vw;
  }

  @media #{$breakpoint-vertical} {
    width: 100%;
    max-width: $card-max-width;

    &.-place,
    &.-economy {
      .c-card__inner {
        font-size: 0.8em;
      }
    }
  }

  @media (min-width: $card-max-width * 2) {
    width: $card-max-width;

    &.-economy-img {
      width: 100%;
    }
  }

  @media #{$breakpoint-horizontal} {
    width: 50vw;

    &.-place,
    &.-economy {
      // max-width: 36em;
      max-width: vh(60);
      --padding-content: 2.6em 3.5em;

      .c-card__inner {
        min-height: vh(85);
        max-height: vh(100);
        font-size: 0.9em;
      }

      &.-simple {
        .c-card__inner {
          min-height: initial;
          max-height: none;
        }
      }
    }

    &.-economy-img {
      .c-card__wrap {
        display: inline-block;
      }

      .c-card__picture {
        width: 100%;
      }

      img {
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: vh(50);
      }
    }
  }

  // Edge clip-path Fix
  .is-edge & {
    .c-card__inner {
      clip-path: none;
      transition: transform 0.3s $in-quad;
    }

    .c-card__corner {
      clip-path: none;
      width: 0;
      height: 0;
      padding-left: 20%;
      padding-bottom: 20%;
      background-color: var(--bg-behind);
      overflow: hidden;
      transition: transform 0.3s ease-out;
      transform-origin: 100% 100%;

      &:after {
        @include pseudo-el($width: 0, $height: 0);
        margin-left: -500px;
        border-bottom: 500px solid transparent;
        border-left: 500px solid var(--bg-corner);
      }
    }

    &.-fact {
      &:hover {
        .c-card__corner {
          transform: scale(1.3);
        }
      }

      &:hover.is-folded {
        .c-card__inner {
          transform: translate(100%, 2em);
        }

        .c-card__corner {
          transform: scale(0);
        }
      }
    }
  }
  // END of dirt
}

.c-card__inner {
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  justify-content: flex-start;
  background-color: var(--bg-inner);
  overflow-x: auto;

  @include hide-scrollbar;
}

.c-card__behind {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: var(--padding-content);
  color: var(--color-text-behind);
  background-color: var(--bg-behind);
  overflow-x: auto;

  > *:nth-child(n + 2) {
    margin-top: 0.5rem;
  }
}

.c-card__corner {
  z-index: 2;
  position: absolute;
  bottom: 0;
  right: 0;
  display: block;
  background-color: var(--bg-corner);
}

.c-card__img {
  @include img;
}
</style>
